import { Tools, ToolKind, type ToolId } from '~~/shared/web-chat/tools'
import { chatDocumentLink } from '~/utils/chatDocumentLinks'

// Id unions split by kind — derived from the registry, no hand-kept lists. A missing or
// extra key in the dispatch maps below (and in the store's CONFIRM map) is a compile
// error, which is what keeps schema and behavior from drifting.
export type SilentId = { [K in ToolId]: typeof Tools[K]['kind'] extends ToolKind.Silent ? K : never }[ToolId]
export type ConfirmId = { [K in ToolId]: typeof Tools[K]['kind'] extends ToolKind.Confirm ? K : never }[ToolId]

/**
 * Resolve a tool name to its registry kind (or undefined if it isn't a client tool).
 *
 * Every client tool is in the static registry again. There used to be a second, dynamic family —
 * `run_workflow__<slug>`, one generated per published workflow (MAR-97) — which had to be
 * recognised by prefix here because it was in no registry to look up. MAR-437 replaced the whole
 * family with a single static `run_workflow`, so the prefix check and its two helpers are gone.
 */
export function clientToolKind(name: string): ToolKind | undefined {
  return (Tools as Record<string, { kind: ToolKind }>)[name]?.kind
}

/**
 * Outcome of `open_case_or_client`. `not_found` is a real answer, not an error: the id came from the
 * model, and one it picked up before a case was deleted — or from another org — must NOT silently
 * leave the chat where it was while the agent reports having moved it.
 */
export type ScopeOpenResult =
  | { ok: true; kind: 'case' | 'client'; name: string }
  | { ok: false; reason: 'not_found' | 'unavailable' }

export interface SilentDispatchDeps {
  router: { push: (to: any) => void }
  requestHighlight: (req: { text: string; page?: number; label?: string }) => void
  /** `filterFiles` moves the file explorer's own search box, not the store's server-search field —
   *  the two are different lenses and only one of them is on screen (MAR-494). Its return says
   *  whether it actually reached that box — `false` means the explorer is not mounted, and the tool
   *  must report that honestly rather than the generic success (AlexAmin/marcus#795). `selectFiles`
   *  answers the same question for a selection (MAR-991): `false` means nothing on screen shows it. */
  browser: { filterFiles: (q: string) => boolean; selectFiles: (ids: string[]) => boolean }
  requestSheetFilter: (query: string) => void
  requestSheetSelect: (ids: string[]) => void
  openScope: (target: { kind: 'case' | 'client'; id: string }) => Promise<ScopeOpenResult>
}

/** What a silent tool reports back to the paused ADK session. `undefined` → the generic `{ok:true}`. */
export type SilentResult = void | Promise<void> | Promise<unknown> | Record<string, unknown>

// Silent tools execute immediately in the UI. The tool name IS the id; `input` is the
// flat params object. Exhaustive over SilentId — adding a silent tool to the registry
// breaks the build here until its handler exists.
//
// A handler may return a value (or a promise of one): that value becomes the tool's
// functionResponse, so a tool that can FAIL — `open_case_or_client` — says so instead of
// letting the caller post a blanket `{ok:true}` the agent would then narrate as success.
export function createSilentDispatch(deps: SilentDispatchDeps): Record<SilentId, (input: any) => SilentResult> {
  return {
    // '' clears. A reported `false` means no mounted file explorer received the filter — say so
    // instead of letting the caller post the blanket `{ok:true}` (AlexAmin/marcus#795).
    filter_files: (p) => {
      const applied = deps.browser.filterFiles(String(p.query ?? ''))
      if (!applied) return { ok: false, reason: 'file_explorer_not_open' }
    },
    select_files: (p) => {
      const shown = deps.browser.selectFiles(Array.isArray(p.document_ids) ? p.document_ids.map(String) : [])
      if (!shown) return { ok: false, reason: 'file_explorer_not_open' }
    },
    filter_sheet: p => deps.requestSheetFilter(String(p.query ?? '')), // '' clears
    select_sheet_rows: p => deps.requestSheetSelect(Array.isArray(p.document_ids) ? p.document_ids.map(String) : []),
    open_document: p => deps.router.push(chatDocumentLink(String(p.document_id ?? ''))),
    navigate_to: p => deps.router.push({ path: p.path, query: p.query }),
    highlight_text: p => deps.requestHighlight({ text: p.text, page: p.page, label: p.label }),
    open_case_or_client: p => deps.openScope({
      kind: p.kind === 'client' ? 'client' : 'case',
      id: String(p.id ?? ''),
    }),
  }
}
